import React, { useEffect } from 'react';
import {
  ExternalLink,
  Clapperboard,
  Check,
  X,
  ShieldCheck,
} from 'lucide-react';
import Reveal from './Reveal';
import PatternBg from './PatternBg';
import YouTubeLite from './YouTubeLite';
import {
  CLIENT_LOGOS,
  VIDEO_TESTIMONIALS,
  TRUSTPILOT,
  VERIFIED_RESULTS,
  NUOLA_CASE,
  RESULT_ASSETS,
  COMPARISON,
  KLAVIYO_BADGE,
} from '../mock';

const TP_GREEN = '#00b67a';

/* ---------- centred brand lockup (logos, no nav bar) ---------- */
function BrandLockup() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-x-8">
      <div className="flex items-center gap-2 select-none" aria-label="OnBrand Emails">
        <span className="relative flex h-6 w-11 items-center rounded-full bg-[var(--ob-ink)] px-1">
          <span className="h-4 w-4 rounded-full bg-[var(--ob-accent)]" />
        </span>
        <span className="font-display text-2xl font-extrabold tracking-tight leading-none text-[var(--ob-ink)]">
          ONBRAND<span className="text-[var(--ob-accent)]">.</span>
        </span>
      </div>
      <span className="hidden h-8 w-px bg-[var(--ob-line)] sm:block" aria-hidden="true" />
      <img
        src={KLAVIYO_BADGE}
        alt="Official Klaviyo Partner"
        className="h-9 w-auto opacity-90"
        draggable={false}
      />
    </div>
  );

}

/* ---------- Trustpilot fractional stars ---------- */
function TrustStars({ value = 5, size = 20 }) {
  return (
    <span className="flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, value - i));
        return (
          <span
            key={i}
            className="relative inline-flex items-center justify-center rounded-[3px]"
            style={{ width: size, height: size, backgroundColor: '#dcdce6' }}
          >
            <span
              className="absolute inset-y-0 left-0 overflow-hidden rounded-l-[3px]"
              style={{ width: `${fill * 100}%`, backgroundColor: TP_GREEN }}
            />
            <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} className="relative" fill="#fff">
              <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z" />
            </svg>
          </span>
        );
      })}
    </span>
  );
}

/* ---------- Section 1: confirmation hero ---------- */
function ConfirmationHero() {
  const steps = [
    'Look For An Email With Your Calendar Invite',
    'Accept The Invitation',
    'Scroll Below For FAQs, Client Reviews & Testimonials',
  ];
  return (
    <section className="relative overflow-hidden bg-[var(--ob-bg)] px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-16">
      <PatternBg
        size={165}
        opacity={0.08}
        mask="radial-gradient(90% 90% at 50% 25%, #000 20%, transparent 70%)"
      />
      <div className="relative mx-auto max-w-[820px] text-center">
        <Reveal>
          <BrandLockup />
          <p className="mt-9 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ob-ink-soft)]">
            Trusted By 100+ E-commerce Brands
          </p>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[var(--ob-ink)] sm:text-5xl lg:text-6xl">
            Your Call Has Been Scheduled!
          </h1>
        </Reveal>

        <Reveal delay={80}>
          <h2 className="mt-12 font-display text-xl font-bold text-[var(--ob-ink)] md:text-2xl">
            Follow The Steps Below:
          </h2>
          <div className="mx-auto mt-7 max-w-xl space-y-4">
            {steps.map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-center gap-3 text-base text-[var(--ob-ink)] md:text-lg"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ob-accent)]/15 font-display text-sm font-extrabold text-[var(--ob-accent-dark)]">
                  {i + 1}
                </span>
                <span className="text-left leading-snug">
                  <span className="font-semibold">Step {i + 1}:</span> {s}
                </span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Section 2: client credibility marquee ---------- */
function ClientCredibility() {
  const loop = [...CLIENT_LOGOS, ...CLIENT_LOGOS];
  return (
    <section className="border-y border-[var(--ob-line)] bg-[var(--ob-bg-alt)] py-12">
      <p className="mb-8 text-center text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ob-ink-soft)]">
        You are in good hands · trusted by these Shopify brands
      </p>
      <div className="ob-marquee-wrap ob-fade-x overflow-hidden">
        <div className="ob-marquee ob-marquee--left" style={{ animationDuration: '110s' }}>
          {loop.map((logo, i) => (
            <div
              key={`${logo.name}-${i}`}
              className="mx-4 flex h-20 min-w-[180px] shrink-0 items-center justify-center rounded-2xl bg-white px-8"
            >
              <img
                src={logo.src}
                alt={`${logo.name} logo`}
                loading="lazy"
                draggable={false}
                className={`w-auto object-contain opacity-90 ${logo.imgClass || 'max-h-10'}`}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 3: consolidated FAQ (square expandable cards) ---------- */
const FAQS = [
  {
    id: 'call',
    q: 'What happens on the call?',
    a: "On the call, we'll discuss your brand's high-level metrics, including customer acquisition costs, the size of your email list and your current retention performance. We'll also talk through the challenges you're facing and identify where the biggest opportunities might be.\n\nFrom there, we'll determine whether OnBrand is the right fit. If it is, we'll typically move forward with a much more extensive audit of your Klaviyo account.",
  },
  {
    id: 'who',
    q: 'Who is OnBrand?',
    a: 'We are an email and SMS retention partner for ecommerce brands, founded by Luke and Jake. We are official Klaviyo partners and generated over $55M for our partners in 2025. You can see genuine client results, video testimonials and Trustpilot reviews further down this page.',
  },
  {
    id: 'manage',
    q: 'What exactly do you manage?',
    a: 'Full-scope Klaviyo email and SMS: strategy, copy, design, flow and campaign builds, testing and ongoing optimisation. We handle the retention channel end to end so your team can stay focused on the product.',
  },
  {
    id: 'how',
    q: 'How does working with OnBrand work?',
    a: "After a quick qualification and your free audit, we build a custom roadmap based on our findings, outlining the actions we believe will make the biggest difference to your brand.\n\nThis typically involves rebuilding or optimising your flows, forms, email design and deliverability, followed by ongoing testing and optimisation.\n\nWe keep communication fast and report consistently, so you always know what we're working on and what comes next.",
  },
  {
    id: 'results',
    q: 'What results can I expect?',
    a: "Results vary from brand to brand, so it's difficult to promise a specific number before we've reviewed your account.\n\nOnce we've completed your audit and built your custom roadmap, we can use our experience and findings to provide revenue projections for your brand.\n\nIn many cases, we're also confident enough in those findings to offer specific revenue or percentage-based guarantees. Any projections or guarantees are discussed individually and depend on what we uncover during your audit.",
  },
  {
    id: 'free',
    q: 'Is the audit free, and is there any obligation?',
    a: 'Yes, the audit is genuinely free and there is no obligation to continue afterwards. Worst case, you walk away with a roadmap you can run yourself or hand to your team.',
  },
  {
    id: 'size',
    q: 'What size brands do you work with?',
    a: "We primarily work with 8- to 9-figure Shopify brands, but we also partner with some fantastic 7-figure brands.\n\nFor smaller brands, what matters most is whether we can identify meaningful opportunities for growth and genuinely help them move forward.",
  },
  {
    id: 'agency',
    q: 'What if I already have an in-house team or agency?',
    a: "That's not necessarily a problem. Our free audit gives you an independent view of what's working, what isn't and where there may be opportunities to improve, drawing on our experience working with over 100 brands.\n\nIt doesn't always have to be a choice between your existing team and OnBrand. Depending on your setup, we may be able to work alongside your team, support specific areas of your retention strategy or take over the management of your email and SMS channels.\n\nWe'll discuss the options during the call and recommend an approach based on your brand's needs.",
  },
  {
    id: 'klaviyo',
    q: 'Do you only work with Klaviyo?',
    a: "We specialise in Klaviyo and are official Klaviyo partners. It's the platform used by the vast majority of the brands we work with.\n\nHowever, we do work with other email service providers as well. If you're using another platform, we can discuss your current setup and whether we're the right fit during the call.",
  },
  {
    id: 'after',
    q: 'What happens after the call?',
    a: "If we both feel there's a good fit, the next step is an in-depth audit of your account. This typically takes us a day or two to complete.\n\nWe'll then arrange a follow-up call to walk you through our findings, explain the opportunities we've identified and present a custom roadmap for your brand.\n\nFrom there, we can discuss what working together would look like and agree on the next steps.",
  },
];

function FaqCard({ item, index }) {
  return (
    <details
      className="group ob-faq-card flex flex-col rounded-3xl border border-[var(--ob-line)] bg-white transition-colors duration-300 open:border-[var(--ob-accent-dark)]/40 [&_summary]:list-none"
      data-testid={`ty-faq-${item.id}`}
    >
      <summary className="flex cursor-pointer flex-col gap-3 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ob-accent-dark)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ob-bg)] md:p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="font-display text-sm font-extrabold text-[var(--ob-accent-dark)] transition-colors duration-300 group-hover:text-[var(--ob-ink)]">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--ob-line)] text-xl leading-none text-[var(--ob-ink)] transition-transform duration-300 group-open:rotate-45 group-hover:border-[var(--ob-ink)]"
            aria-hidden="true"
          >
            +
          </span>
        </div>
        <h3 className="font-display text-lg font-bold leading-snug text-[var(--ob-ink)] md:text-xl">
          {item.q}
        </h3>
      </summary>
      <div className="px-5 pb-5 md:px-6 md:pb-6">
        <p className="whitespace-pre-line border-t border-[var(--ob-line)] pt-4 text-sm leading-relaxed text-[var(--ob-ink-soft)] transition-colors duration-300 group-hover:border-[var(--ob-ink)]/20 group-hover:text-[var(--ob-ink)]">
          {item.a}
        </p>
      </div>
    </details>
  );
}

function FaqSection() {
  return (
    <section className="relative overflow-hidden bg-[var(--ob-bg)] py-20 md:py-28">
      <PatternBg
        size={140}
        opacity={0.06}
        mask="radial-gradient(120% 120% at 15% 0%, #000 5%, transparent 65%)"
      />
      <div className="relative mx-auto max-w-[1280px] px-5 md:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ob-accent-dark)]">
            Before we talk
          </p>
          <h2 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-[var(--ob-ink)] md:text-5xl">
            A few common questions, answered.
          </h2>
          <p className="mt-5 text-lg text-[var(--ob-ink-soft)]">
            The things people most often want to know before an audit. Tap any card to read the
            answer.
          </p>
        </Reveal>
        <div
          className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="ty-faqs"
        >
          {FAQS.map((item, i) => (
            <Reveal key={item.id} delay={(i % 4) * 70}>
              <FaqCard item={item} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 4: two genuine video testimonials ---------- */
function VideoTestimonials() {
  return (
    <section className="bg-[var(--ob-bg-alt)] py-20 md:py-28">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ob-accent-dark)]">
            In their words
          </p>
          <h2 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-[var(--ob-ink)] md:text-5xl">
            Founders on working with OnBrand.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {VIDEO_TESTIMONIALS.map((item, i) => (
            <Reveal key={i} delay={i * 100}>
              <figure className="flex h-full flex-col overflow-hidden rounded-3xl border border-[var(--ob-line)] bg-white">
                <div className="relative aspect-video w-full bg-[var(--ob-dark)]">
                  <YouTubeLite videoId={item.videoId} title={`OnBrand ${item.label}`} size="md" />
                </div>
                <figcaption className="flex items-center gap-3 px-6 py-5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ob-accent)]/15 text-[var(--ob-accent-dark)]">
                    <Clapperboard size={16} />
                  </span>
                  <div>
                    <div className="font-display text-sm font-bold uppercase tracking-wide text-[var(--ob-ink)]">
                      {item.label}
                    </div>
                    <div className="text-sm text-[var(--ob-ink-soft)]">{item.caption}</div>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 5: Trustpilot continuous review marquee ---------- */
function TrustpilotMarquee() {
  const { reviews, score, label, totalReviews, scoreValue, profileUrl, logo } = TRUSTPILOT;
  // Duplicate the 8 genuine reviews once ONLY to create a seamless CSS loop.
  const loop = [...reviews, ...reviews];
  return (
    <section className="bg-[var(--ob-bg)] py-20 md:py-28">
      <div className="mx-auto mb-10 max-w-[1280px] px-5 md:px-8">
        <Reveal>
          <div className="flex flex-col items-start gap-5 rounded-3xl border border-[var(--ob-line)] bg-white px-6 py-6 sm:flex-row sm:items-center sm:justify-between md:px-8">
            <div className="flex items-center gap-5">
              <div className="flex flex-col items-center leading-none">
                <span className="font-display text-5xl font-extrabold text-[var(--ob-ink)]">{score}</span>
                <span className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ob-ink-soft)]">
                  out of 5
                </span>
              </div>
              <div className="h-14 w-px bg-[var(--ob-line)]" />
              <div>
                <TrustStars value={scoreValue} size={24} />
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-display text-base font-bold text-[var(--ob-ink)]">{label}</span>
                  <span className="text-sm text-[var(--ob-ink-soft)]">Based on {totalReviews} reviews on</span>
                  {logo ? (
                    <img src={logo} alt="Trustpilot" className="h-5 w-auto" />
                  ) : (
                    <span className="text-sm font-semibold text-[var(--ob-ink-soft)]">Trustpilot</span>
                  )}
                </div>
              </div>
            </div>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ty-trustpilot-link"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--ob-dark)] px-5 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03]"
            >
              Read our reviews on Trustpilot
              <ExternalLink size={15} />
            </a>
          </div>
        </Reveal>
      </div>

      <div className="ob-marquee-wrap ob-fade-x overflow-hidden" data-testid="ty-trustpilot-marquee">
        <div className="ob-marquee ob-marquee--left" style={{ animationDuration: '90s' }}>
          {loop.map((review, i) => (
            <figure
              key={`${review.id}-${i}`}
              className="mx-3 flex h-[440px] w-[300px] shrink-0 flex-col overflow-hidden rounded-3xl border border-[var(--ob-line)] bg-white sm:w-[340px]"
              aria-hidden={i >= reviews.length ? 'true' : undefined}
            >
              <figcaption className="flex items-center justify-between gap-3 border-b border-[var(--ob-line)] px-5 py-3">
                <div className="min-w-0">
                  <div className="truncate font-display text-sm font-bold text-[var(--ob-ink)]">
                    {review.name}
                  </div>
                  <div className="truncate text-xs text-[var(--ob-ink-soft)]">{review.title}</div>
                </div>
                <TrustStars value={review.rating} size={15} />
              </figcaption>
              <div className="flex-1 overflow-hidden bg-white p-3">
                <img
                  src={review.image}
                  alt={review.alt}
                  loading="lazy"
                  className="block h-auto w-full rounded-xl object-top"
                />
              </div>
            </figure>
          ))}
        </div>
      </div>
      <p className="mt-6 px-5 text-center text-xs text-[var(--ob-ink-soft)]">
        A showcase of our genuine Trustpilot reviews.
      </p>
    </section>
  );
}

/* ---------- Section 6: case studies (3 genuine before/after) ---------- */
function CaseStudies() {
  const nuolaKey = [
    NUOLA_CASE.metrics.find((m) => m.label === 'Email Revenue'),
    NUOLA_CASE.metrics.find((m) => m.label === 'Email Attribution'),
    NUOLA_CASE.metrics.find((m) => m.label === 'Total Revenue'),
  ];
  return (
    <section className="relative overflow-hidden bg-[var(--ob-bg-alt)] py-20 md:py-28">
      <PatternBg
        size={150}
        opacity={0.06}
        mask="radial-gradient(120% 120% at 85% 100%, #000 8%, transparent 68%)"
      />
      <div className="relative mx-auto max-w-[1280px] px-5 md:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ob-accent-dark)]">
            Here is what we have actually done
          </p>
          <h2 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-[var(--ob-ink)] md:text-5xl">
            Real before and after results.
          </h2>
        </Reveal>

        {/* Featured: Nuola Wigs */}
        <Reveal>
          <div className="grid grid-cols-1 gap-8 rounded-[2rem] border border-[var(--ob-line)] bg-white p-6 md:grid-cols-2 md:p-9">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ob-accent-dark)]">
                Case study · {NUOLA_CASE.brand}
              </span>
              <h3 className="mt-3 font-display text-2xl font-extrabold leading-snug text-[var(--ob-ink)] md:text-3xl">
                {NUOLA_CASE.kicker}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[var(--ob-ink-soft)]">{NUOLA_CASE.intro}</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {nuolaKey.map((m) => (
                  <div key={m.label} className="rounded-2xl bg-[var(--ob-bg-alt)] px-3 py-4 text-center">
                    <div className="font-display text-xl font-extrabold text-[var(--ob-accent-dark)]">
                      {m.change}
                    </div>
                    <div className="mt-1 text-[11px] leading-tight text-[var(--ob-ink-soft)]">{m.label}</div>
                    <div className="mt-1 text-[11px] text-[var(--ob-ink-soft)]/80">
                      {m.before} → {m.after}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-[var(--ob-line)]">
              <img
                src={RESULT_ASSETS.nuola}
                alt={`Before and after email performance results for ${NUOLA_CASE.brand}`}
                loading="lazy"
                className="block w-full"
              />
            </div>
          </div>
        </Reveal>

        {/* Two more genuine before/after graphics */}
        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
          {VERIFIED_RESULTS.map((r, i) => (
            <Reveal key={r.id} delay={i * 100}>
              <div className="flex h-full flex-col rounded-[2rem] border border-[var(--ob-line)] bg-white p-6 md:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ob-accent-dark)]">
                      {r.brand}
                    </span>
                    <div className="text-sm text-[var(--ob-ink-soft)]">{r.tag}</div>
                  </div>
                  <span className="rounded-full bg-[var(--ob-accent)]/15 px-4 py-2 font-display text-lg font-extrabold text-[var(--ob-accent-dark)]">
                    {r.highlight}
                  </span>
                </div>
                <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--ob-line)]">
                  <img
                    src={r.img}
                    alt={`Before and after email performance results for ${r.brand}`}
                    loading="lazy"
                    className="block w-full"
                  />
                </div>
                <p className="mt-5 text-sm leading-relaxed text-[var(--ob-ink-soft)]">{r.context}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 8: why OnBrand / positioning ---------- */
function Positioning() {
  const { inhouse, onbrand } = COMPARISON;
  const others = [
    'One playbook applied to every brand',
    'Email is one of several services they sell',
    'Slow replies and generic monthly reports',
    'Leans on discounts to force sales',
  ];
  const ob = [
    'Strategy built around your brand and margins',
    'Email and SMS retention is all we do',
    'Fast, consistent comms with a clear plan each month',
    'Profit-first sending, not a discount crutch',
  ];
  return (
    <section className="bg-[var(--ob-dark)] py-20 text-white md:py-28">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ob-accent)]">
            Why OnBrand
          </p>
          <h2 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight md:text-5xl">
            Typical email management vs a retention partner.
          </h2>
          <p className="mt-5 text-lg text-white/60">
            Most brands do not need another vendor sending emails. They need a retention team that
            treats the channel like real revenue.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-7">
              <h3 className="font-display text-lg font-bold text-white/70">Typical email management</h3>
              <ul className="mt-5 space-y-3">
                {others.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-sm text-white/55">
                    <X size={18} className="mt-0.5 shrink-0 text-white/30" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="h-full rounded-3xl border border-[var(--ob-accent)]/30 bg-[var(--ob-accent)]/[0.06] p-7">
              <h3 className="font-display text-lg font-bold text-[var(--ob-accent)]">The OnBrand approach</h3>
              <ul className="mt-5 space-y-3">
                {ob.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-sm text-white/85">
                    <Check size={18} className="mt-0.5 shrink-0 text-[var(--ob-accent)]" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Existing in-house vs partner economics (genuine site data) */}
        <Reveal delay={150}>
          <div className="mt-6 grid grid-cols-1 gap-6 rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:grid-cols-2">
            <div>
              <div className="font-display text-sm font-bold text-white/70">{inhouse.title}</div>
              <div className="text-xs text-white/45">{inhouse.subtitle}</div>
              <div className="mt-4 font-display text-3xl font-extrabold text-white/80">{inhouse.total}</div>
            </div>
            <div>
              <div className="font-display text-sm font-bold text-[var(--ob-accent)]">{onbrand.title}</div>
              <div className="text-xs text-white/45">{onbrand.subtitle}</div>
              <div className="mt-4 font-display text-3xl font-extrabold text-[var(--ob-accent)]">
                {onbrand.total}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Section 9: final pre-call reminder ---------- */
function FinalReminder() {
  const prep = [
    'Accept the calendar invite so you get the reminders',
    'Join from somewhere quiet where you can properly talk',
    'If you can, have a few numbers handy: AOV, monthly revenue, email and SMS revenue share, traffic and your current retention setup',
  ];
  return (
    <section className="bg-[var(--ob-bg)] px-5 py-20 md:px-8 md:py-28">
      <div className="mx-auto max-w-[820px]">
        <Reveal>
          <div className="rounded-[2rem] border border-[var(--ob-line)] bg-white p-8 text-center md:p-12">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--ob-accent)]/15 text-[var(--ob-accent-dark)]">
              <ShieldCheck size={26} />
            </span>
            <h2 className="mt-6 font-display text-3xl font-extrabold leading-tight text-[var(--ob-ink)] md:text-4xl">
              That is it. We will see you on the call.
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-[var(--ob-ink-soft)]">
              A little prep goes a long way. None of this is mandatory, it just helps us dive
              straight into your numbers.
            </p>
            <ul className="mx-auto mt-8 max-w-xl space-y-3 text-left">
              {prep.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-2xl bg-[var(--ob-bg-alt)] px-5 py-4">
                  <Check size={18} className="mt-0.5 shrink-0 text-[var(--ob-accent-dark)]" />
                  <span className="text-sm text-[var(--ob-ink)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- simple footer ---------- */
function SimpleFooter() {
  const socials = [
    { label: 'OnBrand on LinkedIn', href: 'https://www.linkedin.com/in/jake-kelly-bb81a3323/', text: 'LinkedIn' },
    { label: 'OnBrand on Instagram', href: 'https://www.instagram.com/onbrand.emails/?hl=en', text: 'Instagram' },
    { label: 'OnBrand on YouTube', href: 'https://youtube.com/@jakeonbrand?si=41VRNkly-7FhxMIa', text: 'YouTube' },
  ];
  return (
    <footer className="bg-[var(--ob-dark)] px-5 py-10 text-white md:px-8">
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-5 md:flex-row">
        <div className="flex items-center gap-2">
          <span className="relative flex h-5 w-9 items-center rounded-full bg-white px-[3px]">
            <span className="h-3.5 w-3.5 rounded-full bg-[var(--ob-accent)]" />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight">
            ONBRAND<span className="text-[var(--ob-accent)]">.</span>
          </span>
        </div>
        <div className="flex gap-5">
          {socials.map((s) => (
            <a
              key={s.text}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              className="text-sm text-white/55 transition-colors hover:text-[var(--ob-accent)]"
            >
              {s.text}
            </a>
          ))}
        </div>
        <span className="text-sm text-white/40">© {new Date().getFullYear()} OnBrand Emails.</span>
      </div>
    </footer>
  );
}

/* ---------- page ---------- */
export default function ThankYouPage() {
  // Keep this confirmation page out of search indexes (unlisted, not secured).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Your Strategy Audit Is Booked · OnBrand Emails';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="App bg-[var(--ob-bg)]">
      <main>
        <ConfirmationHero />
        <ClientCredibility />
        <FaqSection />
        <VideoTestimonials />
        <TrustpilotMarquee />
        <CaseStudies />
        <Positioning />
        <FinalReminder />
      </main>
      <SimpleFooter />
    </div>
  );
}
